import { PrinterOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Button } from "antd";
import { FC, useEffect, useRef, useState } from "react";
import { useReactToPrint } from "react-to-print";
import { bookingService } from "~/services/booking";
import { TWaybillService } from "~/types/waybill-service";
import { cn } from "~/utils";
import { PrintContent } from "./print-content";

type Props = {
  className?: string;
  size?: "small" | "large" | "middle";
  selectedServiceTicketsForPrint: TWaybillService[];
  all?: boolean;
  sortOrder?: "inventoryRef" | "title";
};

export const PrintWaybill: FC<Props> = ({
  className,
  size = "large",
  selectedServiceTicketsForPrint,
  all = false,
  sortOrder = "inventoryRef",
}) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printingServices, setPrintingServices] = useState<TWaybillService[]>([]);
  const [printRequested, setPrintRequested] = useState(false);

  // Reset printingServices when selected tickets change
  useEffect(() => {
    if (!isPrinting) {
      setPrintingServices([]);
      setPrintRequested(false);
    }
  }, [selectedServiceTicketsForPrint, isPrinting]);

  const handlePrintWaybills = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Waybill_AHC_${new Date().toISOString()}`,
    onBeforePrint: () => {
      setIsPrinting(true);
      return Promise.resolve();
    },
    onAfterPrint: () => {
      setIsPrinting(false);
      return Promise.resolve();
    },
  });

  const onPrint = async () => {
    try {
      // Clear previous results and mark that a print was requested
      setPrintingServices([]);
      setPrintRequested(true);
      
      // Get the current service ticket IDs
      const serviceTicketIds = selectedServiceTicketsForPrint.map(
        (ticket) => ticket.id
      );
      
      if (serviceTicketIds.length === 0) {
        console.warn("No service tickets selected for printing");
        return;
      }

      // Fetch the latest data from the API
      const { items: jobTickets } =
        await bookingService.getJobTicketsByServiceTickets({
          serviceTicketIds,
          getAll: true,
        });

      // Process the results
      const printServices =
        jobTickets.flatMap((booking) =>
          booking.serviceTicket
            .filter((ticket) => serviceTicketIds.includes(ticket.id))
            .map((ticket) => ({
              ...ticket,
              clientService: ticket.client,
              booking,
            }))
        ) || [];

      // Update state with the latest data
      setPrintingServices(printServices);

      // Trigger the print after the state has been updated
      setTimeout(() => {
        if (componentRef.current) {
          handlePrintWaybills();
        } else {
          console.error("componentRef is null or content is not available.");
        }
      }, 100);
    } catch (error) {
      console.error("Error preparing print data:", error);
      setPrintRequested(false);
    }
  };

  const mutation = useMutation({
    mutationFn: onPrint,
  });

  return (
    <>
      <Button
        size={size}
        className={cn(
          "border rounded-lg hover:bg-gray-50 bg-white shadow-sm",
          className
        )}
        disabled={selectedServiceTicketsForPrint?.length === 0 || mutation.isPending}
        onClick={() => mutation.mutate()}
        icon={
          mutation.isPending || isPrinting ? (
            <svg
              className="animate-spin h-4 w-4 text-black"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              ></path>
            </svg>
          ) : (
            <PrinterOutlined size={20} className="text-[16px]" />
          )
        }
      >
        {all ? "Print all Waybills" : "Print Waybills"}
      </Button>
      
      {/* Only render print content when we have services to print */}
      {printingServices.length > 0 && (
        <div style={{ display: "none" }}>
          <PrintContent
            selectedServiceTicketsForPrint={printingServices}
            sortOrder={sortOrder}
            ref={componentRef}
          />
        </div>
      )}
      
      {/* Show message when print was requested but no services were found */}
      {printRequested && printingServices.length === 0 && !mutation.isPending && (
        <div className="mt-2 text-red-500 text-sm">
          No service tickets selected for printing
        </div>
      )}
    </>
  );
};
